import { SpatialHash } from './spatial.js';

/** Entity kinds. */
export const KIND = { PLAYER: 0, GRUNT: 1, SERGEANT: 2, OFFICER: 3, GENERAL: 4, ALLY: 5, ALLY_OFFICER: 6, ARCHER: 7, HORSE: 8, SIEGE: 9 };
export const KIND_NAME = ['player', 'grunt', 'sergeant', 'officer', 'general', 'ally', 'allyOfficer', 'archer', 'horse', 'siege'];

/** Team ids. 0 = player (Wilwatikta), 1 = enemy (Segara Kidul), 2 = neutral. */
export const TEAM = { PLAYER: 0, ENEMY: 1, NEUTRAL: 2 };

/** Finite state ids shared by AI, animation and combat. */
export const ST = {
  IDLE: 0, WALK: 1, RUN: 2, ATTACK: 3, WINDUP: 4, RECOVER: 5, HIT: 6, LAUNCH: 7,
  AIRBORNE: 8, DOWN: 9, GETUP: 10, BLOCK: 11, GUARD_BREAK: 12, DEAD: 13,
  SPAWN: 14, DODGE: 15, STUN: 16, CHARGE: 17, TAUNT: 18, FLEE: 19, MOUNTED: 20, CELEBRATE: 21,
};
export const ST_NAME = ['idle','walk','run','attack','windup','recover','hit','launch','airborne','down','getup','block','guardBreak','dead','spawn','dodge','stun','charge','taunt','flee','mounted','celebrate'];

/** Bit flags. */
export const FLAG = {
  NONE: 0, HYPER: 1 << 0, INVULN: 1 << 1, SUPERARMOR: 1 << 2, AGGRO: 1 << 3,
  IN_CAMERA: 1 << 4, RAGDOLL: 1 << 5, ELITE: 1 << 6, BANNER: 1 << 7,
  SHIELD: 1 << 8, RANGED: 1 << 9, ON_FIRE: 1 << 10, FROZEN: 1 << 11, CAPTAIN: 1 << 12,
  BOSS: 1 << 13, FADING: 1 << 14, NOCLIP: 1 << 15, JUGGLED: 1 << 16,
};

const CAP = 2600;

/**
 * Stable battle-local key for authored variation. Unlike `uid`, this is
 * intentionally reproducible for the same battle seed and spawn order.
 */
function battleSpawnKey(seed, ordinal) {
  let n = (seed >>> 0) ^ Math.imul(ordinal >>> 0, 0x9e3779b9);
  n = Math.imul(n ^ (n >>> 16), 0x21f0aaad);
  n = Math.imul(n ^ (n >>> 15), 0x735a2d97);
  n = (n ^ (n >>> 15)) >>> 0;
  return n || 0x6d2b79f5;
}

/**
 * Structure-of-arrays entity store. Hot fields live in typed arrays so the
 * crowd renderer and AI can sweep thousands of units without chasing pointers.
 * Rich, rarely-touched data lives in `ext[id]`.
 */
export class Entities {
  constructor(capacity = CAP, worldHalf = 260) {
    const n = this.capacity = capacity;
    const f32 = () => new Float32Array(n);
    const i32 = () => new Int32Array(n);
    this.x = f32(); this.y = f32(); this.z = f32();
    this.vx = f32(); this.vy = f32(); this.vz = f32();
    this.yaw = f32(); this.yawGoal = f32(); this.pitch = f32(); this.roll = f32();
    /* Root-level tumble for a body in the air. `pitch`/`roll` are the
     * accumulated angles the crowd instancer folds into its matrix; these are
     * their rates. Only `src/ai/steering.js` integrates them, only while the
     * body is off the ground, and the renderer fades them out as it lands so
     * the knockdown/death clips own the resting pose (they take the hips to
     * -90 deg themselves). Zero for every unit that is standing on the floor. */
    this.pitchVel = f32(); this.rollVel = f32();
    this.hp = f32(); this.maxHp = f32();
    this.radius = f32(); this.height = f32(); this.scale = f32(); this.mass = f32();
    this.stateT = f32(); this.animT = f32(); this.animSpeed = f32(); this.animBlend = f32();
    this.stagger = f32(); this.poise = f32(); this.maxPoise = f32();
    this.cd = f32(); this.aiT = f32(); this.think = f32(); this.speed = f32();
    this.fade = f32(); this.spawnT = f32(); this.flash = f32(); this.morale = f32();
    this.kind = i32(); this.team = i32(); this.state = i32(); this.prevState = i32();
    this.anim = i32(); this.prevAnim = i32(); this.target = i32(); this.flags = i32();
    this.archetype = i32(); this.group = i32(); this.hitToken = i32(); this.variant = i32();
    this.alive = new Uint8Array(n);
    this.ext = new Array(n).fill(null);

    this._free = new Int32Array(n);
    for (let i = 0; i < n; i++) this._free[i] = n - 1 - i;
    this._freeN = n;
    this.live = new Int32Array(n);
    this.liveN = 0;
    // `uid` is an ABA guard. It is process-global and is never rewound by a
    // mission retry. Reproducible behaviour belongs to spawnKey instead.
    this._nextUid = 1;
    this.uid = i32();
    this.spawnOrdinal = new Uint32Array(n);
    this.spawnKey = new Uint32Array(n);
    this._battleSeed = 20240819;
    this._nextSpawnOrdinal = 1;

    this.hash = new SpatialHash(4.5, worldHalf);
    this._q = new Int32Array(1024);
    /** counts by team/kind for HUD + director */
    this.stats = { total: 0, byTeam: [0, 0, 0], kills: 0, allyKills: 0 };
  }

  spawn(kind, team, x, y, z) {
    if (this._freeN === 0) return -1;
    const id = this._free[--this._freeN];
    this.alive[id] = 1;
    this.uid[id] = this._nextUid++;
    const ordinal = this._nextSpawnOrdinal++;
    this.spawnOrdinal[id] = ordinal;
    this.spawnKey[id] = battleSpawnKey(this._battleSeed, ordinal);
    this.kind[id] = kind; this.team[id] = team;
    this.x[id] = x; this.y[id] = y; this.z[id] = z;
    this.vx[id] = 0; this.vy[id] = 0; this.vz[id] = 0;
    this.yaw[id] = 0; this.yawGoal[id] = 0; this.pitch[id] = 0; this.roll[id] = 0;
    this.pitchVel[id] = 0; this.rollVel[id] = 0;
    this.hp[id] = 0; this.maxHp[id] = 0;
    this.radius[id] = 0; this.height[id] = 0;
    this.state[id] = ST.SPAWN; this.prevState[id] = ST.SPAWN; this.stateT[id] = 0;
    this.anim[id] = 0; this.prevAnim[id] = 0; this.animT[id] = 0; this.animSpeed[id] = 1; this.animBlend[id] = 1;
    this.stagger[id] = 0; this.poise[id] = 0; this.maxPoise[id] = 0;
    this.cd[id] = 0; this.aiT[id] = 0; this.think[id] = 0; this.speed[id] = 0;
    this.fade[id] = 1; this.spawnT[id] = 0; this.flash[id] = 0; this.morale[id] = 0.5;
    this.target[id] = -1; this.flags[id] = 0; this.group[id] = -1; this.hitToken[id] = 0;
    this.scale[id] = 1; this.mass[id] = 1; this.variant[id] = 0; this.archetype[id] = 0;
    this.ext[id] = null;
    this.live[this.liveN++] = id;
    this.stats.total++; this.stats.byTeam[team]++;
    return id;
  }

  /**
   * Clear the battle population while retaining one stable entity slot.
   *
   * UIDs deliberately remain globally monotonic: they only answer "is this
   * still the same occupant of this recycled slot?". Battle-local spawn
   * ordinals/keys restart from the supplied seed so authored variation and AI
   * behaviour reproduce on a same-seed retry without weakening that guard.
   * Rebuilding both lists is important: repeatedly calling `release()` keeps
   * historical release order and changes which slots authored spawns receive.
   */
  reset(keepId = -1, battleSeed = this._battleSeed) {
    const keep = keepId >= 0 && keepId < this.capacity ? keepId | 0 : -1;
    const keepExt = keep >= 0 ? this.ext[keep] : null;
    const keepTeam = keep >= 0 ? this.team[keep] | 0 : TEAM.NEUTRAL;
    const keepUid = keep >= 0 && this.uid[keep] > 0 ? this.uid[keep] : (keep >= 0 ? this._nextUid++ : 0);

    this._battleSeed = (battleSeed >>> 0) || 1;
    this._nextSpawnOrdinal = 1;

    this.alive.fill(0);
    this.uid.fill(0);
    this.spawnOrdinal.fill(0);
    this.spawnKey.fill(0);
    this.hitToken.fill(0);
    this.target.fill(-1);
    this.group.fill(-1);
    this.ext.fill(null);

    this.liveN = 0;
    if (keep >= 0) {
      this.alive[keep] = 1;
      this.uid[keep] = keepUid;
      this.spawnOrdinal[keep] = this._nextSpawnOrdinal++;
      this.spawnKey[keep] = battleSpawnKey(this._battleSeed, this.spawnOrdinal[keep]);
      this.ext[keep] = keepExt;
      this.live[this.liveN++] = keep;
    }

    this._freeN = 0;
    // Descending storage means pop() returns the lowest available id first,
    // matching construction and making slot allocation deterministic.
    for (let id = this.capacity - 1; id >= 0; id--) {
      if (id !== keep) this._free[this._freeN++] = id;
    }
    this.stats.total = keep >= 0 ? 1 : 0;
    this.stats.byTeam[0] = 0; this.stats.byTeam[1] = 0; this.stats.byTeam[2] = 0;
    if (keep >= 0 && keepTeam >= 0 && keepTeam < this.stats.byTeam.length) this.stats.byTeam[keepTeam] = 1;
    this.stats.kills = 0; this.stats.allyKills = 0;
    this.hash.build(this.live, this.liveN, this.x, this.z);
    return keep;
  }

  /** Set the deterministic seed before the first entity is spawned at boot. */
  setBattleSeed(seed) {
    if (this.liveN !== 0) return false;
    this._battleSeed = (seed >>> 0) || 1;
    this._nextSpawnOrdinal = 1;
    return true;
  }

  get battleSeed() { return this._battleSeed; }
  get nextUid() { return this._nextUid; }

  release(id) {
    if (!this.alive[id]) return;
    this.alive[id] = 0;
    this.ext[id] = null;
    this.stats.total--; this.stats.byTeam[this.team[id]]--;
    this._free[this._freeN++] = id;
  }

  /** Call once per fixed step: compacts the live list and rebuilds the hash. */
  refresh() {
    let n = 0;
    const live = this.live, alive = this.alive;
    for (let i = 0; i < this.liveN; i++) { const id = live[i]; if (alive[id]) live[n++] = id; }
    this.liveN = n;
    this.hash.build(live, n, this.x, this.z);
  }

  /** Nearest live entity of `team` within `r`, excluding `self`. Returns id or -1. */
  nearest(x, z, r, team, self = -1, kindMask = -1) {
    const q = this._q;
    const n = this.hash.query(x, z, r, q, q.length);
    let best = -1, bd = r * r;
    for (let i = 0; i < n; i++) {
      const id = q[i];
      if (id === self || !this.alive[id]) continue;
      if (team >= 0 && this.team[id] !== team) continue;
      if (this.state[id] === ST.DEAD) continue;
      if (kindMask >= 0 && ((1 << this.kind[id]) & kindMask) === 0) continue;
      const dx = this.x[id] - x, dz = this.z[id] - z;
      const d = dx * dx + dz * dz;
      if (d < bd) { bd = d; best = id; }
    }
    return best;
  }

  /** Fill `out` with ids within radius; returns count. */
  around(x, z, r, out, max = 256) { return this.hash.query(x, z, r, out, max); }

  countIn(x, z, r, team) {
    const q = this._q, n = this.hash.query(x, z, r, q, q.length);
    let c = 0, r2 = r * r;
    for (let i = 0; i < n; i++) {
      const id = q[i];
      if (!this.alive[id] || this.state[id] === ST.DEAD) continue;
      if (team >= 0 && this.team[id] !== team) continue;
      const dx = this.x[id] - x, dz = this.z[id] - z;
      if (dx * dx + dz * dz <= r2) c++;
    }
    return c;
  }
  has(id, flag) { return (this.flags[id] & flag) !== 0; }
  setFlag(id, flag, on = true) { if (on) this.flags[id] |= flag; else this.flags[id] &= ~flag; }
  isFoe(a, b) { return this.team[a] !== this.team[b] && this.team[b] !== TEAM.NEUTRAL; }
}
